import {
  Bars3BottomLeftIcon,
  ChartBarIcon,
  ExclamationCircleIcon,
  HandThumbUpIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import {
  CheckIcon as CheckIconSolid,
  ExclamationCircleIcon as ExclamationCircleIconSolid,
} from '@heroicons/react/24/solid';
import { useTranslation } from 'react-i18next';
import type { ExamItemAnswerAiCorrectionViewModel } from '@/shared/api/generated/types';
import { formatGrade, normalizeAiGrade } from '../../../../utils';

export interface AiResultDetailsProps {
  /** Resultado da análise devolvido pela IA */
  readonly result: ExamItemAnswerAiCorrectionViewModel;
}

/** Chaves dos critérios avaliados, na ordem em que aparecem na grade. */
const CATEGORY_KEYS = ['content', 'completeness', 'cohesion', 'grammar'] as const;

/**
 * Detalhamento da análise por IA: nota sugerida, critérios, pontos fortes,
 * pontos a melhorar e devolutiva resumida. Renderizado dentro do accordion do
 * `AiCorrectionPanel`.
 */
export function AiResultDetails({ result }: AiResultDetailsProps) {
  const { t } = useTranslation('examCorrection');

  // O endpoint devolve as notas como fração de 0 a 1 — a escala da tela é 0–100
  const categories = CATEGORY_KEYS.map(key => ({
    key,
    score: {
      content: result.contentScore,
      completeness: result.completenessScore,
      cohesion: result.cohesionScore,
      grammar: result.grammarScore,
    }[key],
  }))
    .filter((category): category is { key: typeof category.key; score: number } => {
      return category.score !== undefined;
    })
    .map(category => ({ key: category.key, score: normalizeAiGrade(category.score) }));

  const suggestedGrade =
    result.suggestedGrade === undefined ? undefined : normalizeAiGrade(result.suggestedGrade);

  return (
    <div className="flex flex-col gap-mini pt-mini">
      {suggestedGrade !== undefined && (
        <div className="flex flex-col gap-micro rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xxs">
          <h4 className="flex items-center gap-nano text-ctx-content-title ts-subtitle-xxs">
            <ChartBarIcon className="size-xxs text-ctx-content-base" />
            {t('modal.ai.results.suggestedGrade')}
          </h4>
          <div className="flex items-baseline gap-nano">
            <span className="text-ctx-content-title ts-heading-xl">
              {formatGrade(suggestedGrade)}
            </span>
            <span className="text-ctx-content-base ts-paragraph-xxs">/ 100</span>
          </div>
          <div className="h-[0.375rem] w-full overflow-hidden rounded-pill bg-base-gray-200">
            <div
              className="h-full rounded-pill bg-base-success-500 transition-all duration-500"
              style={{ width: `${suggestedGrade}%` }}
            />
          </div>
        </div>
      )}

      {categories.length > 0 && (
        <div className="flex flex-col gap-micro rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xxs">
          <h4 className="flex items-center gap-nano text-ctx-content-title ts-subtitle-xxs">
            <Squares2X2Icon className="size-xxs text-ctx-content-base" />
            {t('modal.ai.results.categories')}
          </h4>
          <div className="grid grid-cols-2 gap-micro sm:grid-cols-4">
            {categories.map(category => (
              <div
                key={category.key}
                className="flex flex-col gap-nano rounded-sm border-hairline border-ctx-layout-border p-mini"
              >
                <p className="uppercase tracking-wide text-ctx-content-base ts-heading-xxxs">
                  {t(`modal.ai.results.${category.key}` as const)}
                </p>
                <div className="flex items-baseline gap-quark">
                  <span className="text-ctx-content-title ts-heading-sm">
                    {formatGrade(category.score)}
                  </span>
                  <span className="text-ctx-content-base ts-caption-xxs">/ 100</span>
                </div>
                <div className="h-quark w-full overflow-hidden rounded-pill bg-base-gray-200">
                  <div
                    className="h-full rounded-pill bg-ctx-content-primary transition-all duration-500"
                    style={{ width: `${category.score}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {result.strengths && result.strengths.length > 0 && (
        <div className="flex flex-col gap-micro rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xxs">
          <h4 className="flex items-center gap-nano text-ctx-content-title ts-subtitle-xxs">
            <HandThumbUpIcon className="size-xxs text-ctx-content-base" />
            {t('modal.ai.results.strengths')}
          </h4>
          <ul className="flex flex-col gap-micro">
            {result.strengths.map(strength => (
              <li
                key={strength}
                className="flex items-start gap-micro text-ctx-content-title ts-paragraph-xxs"
              >
                <span className="mt-quark flex size-xxs shrink-0 items-center justify-center rounded-circular bg-base-success-100 text-base-success-800">
                  <CheckIconSolid className="size-nano" />
                </span>
                <span>{strength}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {result.improvements && result.improvements.length > 0 && (
        <div className="flex flex-col gap-micro rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xxs">
          <h4 className="flex items-center gap-nano text-ctx-content-title ts-subtitle-xxs">
            <ExclamationCircleIcon className="size-xxs text-ctx-content-base" />
            {t('modal.ai.results.improvements')}
          </h4>
          <ul className="flex flex-col gap-micro">
            {result.improvements.map(improvement => (
              <li
                key={improvement}
                className="flex items-start gap-micro text-ctx-content-title ts-paragraph-xxs"
              >
                <span className="mt-quark flex size-xxs shrink-0 items-center justify-center rounded-circular bg-base-warning-100 text-base-warning-800">
                  <ExclamationCircleIconSolid className="size-nano" />
                </span>
                <span>{improvement}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {result.justification && (
        <div className="flex flex-col gap-micro rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xxs">
          <h4 className="flex items-center gap-nano text-ctx-content-title ts-subtitle-xxs">
            <Bars3BottomLeftIcon className="size-xxs text-ctx-content-base" />
            {t('modal.ai.results.summary')}
          </h4>
          <p className="text-ctx-content-title ts-paragraph-xxs">{result.justification}</p>
        </div>
      )}
    </div>
  );
}
